/* Native links and visible text are the baseline. Enhancements may fail safely. */
(() => {
  'use strict';
  if (window.fsHomeController) { window.fsHomeController.mount(document); return; }
  const instances = new Map();
  function mount(scope) {
    const roots = [...scope.querySelectorAll('[data-fs-home]')];
    if (scope.matches?.('[data-fs-home]')) roots.unshift(scope);
    roots.forEach(root => {
      if (instances.has(root)) return;
      const abort = new AbortController();
      const listen = (target, type, fn, options = {}) => target?.addEventListener(type, fn, {...options, signal:abort.signal});
      const hero = root.querySelector('[data-hero]');
      const video = hero?.querySelector('video');
      const poster = hero?.querySelector('[data-hero-poster]');
      const bar = root.querySelector('[data-nav]');
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      let timer = 0, frame = 0, navFrame = 0, done = false, revealed = false;
      let resizeObserver, intersectionObserver;
      const setState = state => { if (hero) hero.dataset.state = state; };
      function cancelVideoClock() {
        clearTimeout(timer);
        if (frame && video?.cancelVideoFrameCallback) video.cancelVideoFrameCallback(frame);
        frame = 0;
      }
      function settle(state = 'settled') {
        done = true; cancelVideoClock(); video?.pause(); setState(state);
        // The terminal poster remains mounted, reserving the same dimensions.
      }
      function clock(_now, metadata) {
        if (done || !video) return;
        if (metadata.mediaTime >= 1.5) { settle(); return; }
        frame = video.requestVideoFrameCallback(clock);
      }
      function showFrame() {
        if (done || reduce.matches || document.hidden) return;
        clearTimeout(timer); revealed = true; setState('playing');
        if (video.requestVideoFrameCallback) frame = video.requestVideoFrameCallback(clock);
      }
      function start() {
        if (!video || !poster || reduce.matches || navigator.connection?.saveData || document.hidden) return;
        const box = hero.getBoundingClientRect();
        if (box.bottom <= 0 || box.top >= innerHeight) return;
        setState('loading'); timer = setTimeout(() => settle('static'), 2000);
        video.src = video.dataset.src;
        video.muted = true;
        const attempt = video.play();
        attempt?.catch(() => settle('static'));
      }
      listen(video, 'playing', () => {
        if (done) { video.pause(); return; }
        if (video.requestVideoFrameCallback) frame = video.requestVideoFrameCallback(showFrame);
        else showFrame();
      });
      listen(video, 'timeupdate', () => { if (!video.requestVideoFrameCallback && video.currentTime >= 1.5) settle(); });
      listen(video, 'ended', () => settle());
      listen(video, 'error', () => settle('static'));
      listen(reduce, 'change', () => { if (reduce.matches) settle('static'); });
      listen(document, 'visibilitychange', () => { if (document.hidden) settle(); });
      if (hero && 'IntersectionObserver' in window) {
        intersectionObserver = new IntersectionObserver(entries => {
          if (!entries[0].isIntersecting && (revealed || video?.src)) settle();
        });
        intersectionObserver.observe(hero);
      }
      function updateNav() {
        navFrame = 0;
        if (!hero || !bar) return;
        const box = hero.getBoundingClientRect();
        const traversed = -box.top;
        const visible = root.hasAttribute('data-nav-visible');
        if (traversed > box.height * .3) root.setAttribute('data-nav-visible','');
        else if (visible && traversed < box.height * .2 && !bar.contains(document.activeElement)) root.removeAttribute('data-nav-visible');
      }
      function scheduleNav() { if (!navFrame) navFrame = requestAnimationFrame(updateNav); }
      function measureNav() {
        if (bar) root.style.setProperty('--nav-height', `${bar.getBoundingClientRect().height}px`);
        scheduleNav();
      }
      if (bar) {
        root.setAttribute('data-nav-enhanced','');
        listen(window,'scroll',scheduleNav,{passive:true}); listen(window,'resize',measureNav,{passive:true});
        listen(bar,'focusin',() => root.setAttribute('data-nav-visible',''));
        listen(bar,'focusout',scheduleNav);
        if ('ResizeObserver' in window) { resizeObserver = new ResizeObserver(measureNav); resizeObserver.observe(bar); }
        measureNav(); updateNav();
      }
      root.querySelectorAll('[data-product-image]').forEach(img => {
        const failed = () => { img.hidden = true; const fallback = img.parentElement.querySelector('[data-image-fallback]'); if (fallback) fallback.hidden = false; };
        listen(img,'error',failed);
        if (img.complete && !img.naturalWidth) failed();
      });
      listen(root,'click', event => {
        const anchor = event.target.closest('a[href="#MainContent"]');
        if (anchor) document.getElementById('MainContent')?.focus({preventScroll:true});
      });
      function expire() {
        root.querySelectorAll('[data-event-end]').forEach(item => {
          if (Date.now() < Number(item.dataset.eventEnd) * 1000) return;
          if (item.contains(document.activeElement)) {
            const status = item.querySelector('[data-event-status]');
            if (status) { status.hidden = false; status.textContent = 'Ended'; }
          } else item.hidden = true;
        });
        const list = root.querySelector('[data-event-list]');
        const empty = root.querySelector('[data-events-empty]');
        if (empty && list) empty.hidden = !!list.querySelector('[data-event-end]:not([hidden])');
      }
      listen(root,'focusout',() => setTimeout(expire,0));
      const expiryTimer = setInterval(expire,30000); expire(); start();
      instances.set(root, () => {
        done=true; cancelVideoClock(); video?.pause(); abort.abort();
        cancelAnimationFrame(navFrame); clearInterval(expiryTimer);
        resizeObserver?.disconnect(); intersectionObserver?.disconnect();
        root.removeAttribute('data-nav-enhanced'); root.removeAttribute('data-nav-visible'); setState('static');
        instances.delete(root);
      });
    });
  }
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [...instances].forEach(([root, cleanup]) => { if (event.target.contains(root)) cleanup(); });
  });
  window.fsHomeController = {mount, unmount: root => instances.get(root)?.()};
  mount(document);
})();
